import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Pricing.css";
import authApi, { PlanResponse } from "../services/authApi.ts";
import paymentService, { RazorpayResponse, RazorpayErrorResponse } from "../services/paymentService.ts";
import { useAuth } from "../context/AuthContext.tsx";
import LoginModal from "./auth/LoginModal.tsx";

const Pricing = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const [plans, setPlans] = useState<PlanResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [processingPlanId, setProcessingPlanId] = useState<number | null>(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [pendingPlanId, setPendingPlanId] = useState<number | null>(null);

    // Redirect if user already has active subscription
    useEffect(() => {
        if (isAuthenticated && user?.isPremium) {
            navigate('/screener/momentum-ai', { replace: true });
        }
    }, [isAuthenticated, user, navigate]);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                setLoading(true);
                const response = await authApi.getPlans();
                setPlans(response.data);
                setError(null);
            } catch (err: any) {
                console.error('Failed to fetch plans:', err);
                setError('Failed to load pricing plans. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, []);

    if (loading) {
        return (
            <div className="main-content pricing-container">
                <div className="pricing-header">
                    <h1 className="pricing-title">Loading Plans...</h1>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main-content pricing-container">
                <div className="pricing-header">
                    <h1 className="pricing-title">Error</h1>
                    <p className="pricing-subtitle" style={{ color: 'red' }}>{error}</p>
                </div>
            </div>
        );
    }

    const initiatePaymentFlow = async (planId: number) => {
        try {
            setProcessingPlanId(planId);

            // Initiate payment
            await paymentService.initiatePayment(
                planId,
                // On success callback
                (response: RazorpayResponse) => {
                    console.log('Payment successful:', response);
                    // Navigate to success page with payment response
                    navigate('/payment/success', {
                        state: { paymentResponse: response }
                    });
                },
                // On failure callback
                (error: RazorpayErrorResponse) => {
                    console.error('Payment failed:', error);
                    // Navigate to failure page with error details
                    navigate('/payment/failed', {
                        state: { errorResponse: error }
                    });
                }
            );
        } catch (err: any) {
            console.error('Error initiating payment:', err);
            alert('Failed to initiate payment. Please try again.');
        } finally {
            setProcessingPlanId(null);
        }
    };

    const handleSubscribe = async (planId: number) => {
        // Check if user is authenticated
        if (!isAuthenticated) {
            // Show login modal instead of redirecting
            setPendingPlanId(planId);
            setShowLoginModal(true);
            return;
        }

        // User is authenticated, proceed with payment
        await initiatePaymentFlow(planId);
    };

    const handleLoginSuccess = () => {
        // Close modal after successful login
        // User will need to click Subscribe again to initiate payment
        setShowLoginModal(false);
        setPendingPlanId(null);
    };

    const handleCloseModal = () => {
        setShowLoginModal(false);
        setPendingPlanId(null);
    };

    return (
        <div className="main-content pricing-container">
            <div className="pricing-header">
                <h1 className="pricing-title">Choose Your Plan</h1>
                <p className="pricing-subtitle">Unlock premium features and take your trading to the next level</p>
            </div>

            <div className="pricing-grid">
                {plans.map((plan, index) => {
                    const isPopular = plan.planName.toLowerCase().includes('yearly') && plan.durationMonths === 6;
                    const perMonth = Math.round(plan.price / plan.durationMonths);
                    const savings = plan.discountPercent > 0 ? `${plan.discountPercent}% OFF` : null;

                    return (
                        <div
                            key={plan.id}
                            className={`pricing-card ${isPopular ? "popular" : ""}`}
                        >
                            {isPopular && <div className="popular-badge">Most Popular</div>}
                            {savings && <div className="savings-badge">{savings}</div>}

                            <h2 className="plan-name">{plan.planName}</h2>
                            <p className="plan-duration">{plan.durationMonths} Months</p>

                            <div className="plan-pricing">
                                <span className="original-price">₹{plan.originalPrice}</span>
                                <span className="current-price">₹{plan.price}</span>
                                <span className="gst-text">GST included</span>
                            </div>

                            <p className="per-month">₹{perMonth}/month</p>

                            <ul className="plan-features">
                                {plan.features && plan.features.map((feature, i) => (
                                    <li key={i}>
                                        <span className="feature-check">✓</span>
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <button
                                className={`subscribe-btn ${isPopular ? "primary" : ""}`}
                                onClick={() => handleSubscribe(plan.id)}
                                disabled={processingPlanId === plan.id}
                            >
                                {processingPlanId === plan.id ? 'Processing...' : (isPopular ? 'Subscribe Now' : 'Subscribe')}
                            </button>
                        </div>
                    );
                })}
            </div>

            <p className="pricing-note">All prices are inclusive of GST.</p>

            {/* Login Modal - Google Only for Subscribe */}
            <LoginModal
                isOpen={showLoginModal}
                onClose={handleCloseModal}
                onLoginSuccess={handleLoginSuccess}
                googleOnly={true}
            />
        </div>
    );
};

export default Pricing;
